<template lang="pug">
.login-qr-code(:data-role='userRole')
  .login-qr-code__title
    image-base.login-qr-code__title__logo(:src='require("@/assets/img/common/logo.svg")')
    .login-qr-code__title__text
      slot(name='loginTitle')
  .login-qr-code__content
    .login-qr-code__content__error.line-height-thin
      .login-qr-code__content__error__message.text-xsmall(v-for='message in errorMessages') {{ message }}
    //- QRコードリーダー部
    .login-qr-code__content__reader
      //- カメラモニター画像表示部
      .login-qr-code__content__reader__camera(v-if='showCameraView')
        .login-qr-code__content__reader__camera__title
          slot(name='cameraTitle')
        .login-qr-code__content__reader__camera__monitor
          //- カメラモニター画像表示キャンバス
          canvas.login-qr-code__content__reader__camera__monitor__canvas(ref='cameraMonitor')
          //- カメラローディング表示
          .login-qr-code__content__reader__camera__monitor__loading(v-if='loading')
            .login-qr-code__content__reader__camera__monitor__loading__text.font-bold
              slot(name='cameraLoadingText')
          //- カメラモード切り替えボタン
          button-base.login-qr-code__content__reader__camera__monitor__switch(v-else, @click='switchCameraMode')
            image-base.login-qr-code__content__reader__camera__monitor__switch__icon(
              :src='require("@/assets/img/qr/camera-switch.svg")'
            )
      //- カメラ画面表示開始ボタン表示部
      .login-qr-code__content__reader__start(v-else)
        button-base.login-qr-code__content__reader__start__button(@click='startShowCamera')
          image-base.login-qr-code__content__reader__start__button__icon(:src='require("@/assets/img/qr/qr.png")')
          .login-qr-code__content__reader__start__button__text
            slot(name='startCameraText')
  link-base.login-qr-code__link(@click='moveLinkEvent')
    slot(name='moveLinkText')
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import ImageBase from '@/components/atoms/ImageBase.vue'
import ButtonBase from '@/components/atoms/ButtonBase.vue'
import LinkBase from '@/components/atoms/LinkBase.vue'
import { UserRoles } from '@/const/UserRoles'
import { QrCodeLineSetting } from '@/const/DisplaySettings'
import jsQR, { QRCode } from 'jsqr'

/**
 * 管理画面・ログイン画面（QRコード）
 */
@Component({
  components: {
    ImageBase,
    ButtonBase,
    LinkBase,
  },
})
export default class LoginQrCode extends Vue {
  /** カメラ画面表示フラグ */
  private showCameraView = false
  /** 映像デバイス読み込み中フラグ */
  private loading = false
  /** カメラ映像表示用canvasアニメーションID */
  private animationId?: number

  /** デバイス映像ストリーム用 */
  private videoStreamElem: HTMLVideoElement = document.createElement('video')
  /** カメラ対面モード（初期値）設定 */
  private cameraFacingMode: 'user' | 'environment' = 'environment'

  // ユーザ権限
  @Prop()
  userRole!: UserRoles

  // エラーメッセージ
  @Prop()
  errorMessages!: string[]

  // 移動リンク押下時イベント
  @Prop()
  moveLinkEvent!: Function

  /** カメラ映像描画先キャンバス */
  @Ref() cameraMonitor!: HTMLCanvasElement

  // カメラ画面を表示する
  private async startShowCamera(): Promise<void> {
    this.showCameraView = true
    await this.startMediaDevice()
  }

  // カメラモード切り替え処理
  private async switchCameraMode(): Promise<void> {
    this.cameraFacingMode = this.cameraFacingMode === 'user' ? 'environment' : 'user'
    await this.stopMediaDevice()
    await this.startMediaDevice()
  }

  /**
   * 映像デバイス情報取得・更新、カメラ映像解析処理開始
   */
  public async startMediaDevice(): Promise<void> {
    const video: HTMLVideoElement = this.videoStreamElem
    const canvasAnimationRender: () => void = this.canvasAnimationRender

    this.loading = true
    await navigator.mediaDevices.getUserMedia({ video: { facingMode: this.cameraFacingMode } }).then(function (
      stream: MediaStream
    ): void {
      video.srcObject = stream
      video.setAttribute('playsinline', 'true') // required to tell iOS safari we don't want fullscreen
      video.play()
      // カメラ映像表示用canvasアニメーション開始
      requestAnimationFrame(canvasAnimationRender)
    })
  }

  /**
   * 映像ストリーム準備中判定
   */
  private isVideoOnLoad(): boolean {
    return !!this.videoStreamElem && this.videoStreamElem.readyState === this.videoStreamElem.HAVE_ENOUGH_DATA
  }

  /**
   * カメラ映像表示用canvasアニメーション処理
   */
  private canvasAnimationRender(): void {
    this.animationId = requestAnimationFrame(this.canvasAnimationRender)
    // 要素が失われたらアニメーション停止（ページ遷移対策）
    if (!this.cameraMonitor) {
      this.stopMediaDevice()
      return
    }

    // 映像ストリームが有効になるまで処理しない
    if (this.isVideoOnLoad()) {
      // 映像ストリームサイズを、カメラ映像表示キャンバスサイズに適用
      if (this.loading) {
        this.cameraMonitor.width = this.videoStreamElem.videoWidth
        this.cameraMonitor.height = this.videoStreamElem.videoHeight
        this.loading = false
        return
      }

      // カメラ映像表示用キャンバスに映像ストリーム描画
      const canvas = this.cameraMonitor.getContext('2d', {
        willReadFrequently: true,
      }) as CanvasRenderingContext2D
      canvas.drawImage(this.videoStreamElem, 0, 0, this.cameraMonitor.width, this.cameraMonitor.height)

      // カメラ映像表示用キャンバスから映像情報取得
      const imageData: ImageData = canvas.getImageData(0, 0, this.cameraMonitor.width, this.cameraMonitor.height)
      // QRコード解析
      const qrCode: QRCode | null = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      })

      // QRコード検出判定
      if (qrCode && qrCode.data !== '') {
        // 検出されたQRコード部分にライン描画
        this.drawQrCodeDetectionLine(qrCode.location)
        this.stopMediaDevice()
        this.$emit('read-qr-code', qrCode.data)
      }
    }
  }

  /**
   * 映像デバイス情報取得・更新、カメラ映像解析終了
   */
  private async stopMediaDevice(): Promise<void> {
    // カメラ映像表示用canvasアニメーション停止
    if (this.animationId) cancelAnimationFrame(this.animationId)

    const mediaStream = this.videoStreamElem.srcObject as MediaStream
    if (!mediaStream) return

    // デバイス映像ストリーム終了
    const tracks = mediaStream.getTracks()
    tracks.forEach((track: MediaStreamTrack) => {
      track.stop()
      mediaStream.removeTrack(track)
    })
    this.videoStreamElem.srcObject = null
  }

  /**
   * QRコード検出ライン描画
   */
  private drawQrCodeDetectionLine(qrLocat: QRCode['location']) {
    this.drawLine(qrLocat.topLeftCorner, qrLocat.topRightCorner)
    this.drawLine(qrLocat.topRightCorner, qrLocat.bottomRightCorner)
    this.drawLine(qrLocat.bottomRightCorner, qrLocat.bottomLeftCorner)
    this.drawLine(qrLocat.bottomLeftCorner, qrLocat.topLeftCorner)
  }

  /**
   * カメラ映像表示用キャンバスにライン描画
   */
  private drawLine(begin: { x: number; y: number }, end: { x: number; y: number }) {
    const canvas = this.cameraMonitor.getContext('2d') as CanvasRenderingContext2D

    canvas.beginPath()
    canvas.moveTo(begin.x, begin.y)
    canvas.lineTo(end.x, end.y)
    canvas.lineWidth = QrCodeLineSetting.QR_CODE_LINE_DRAW_WIDTH
    canvas.strokeStyle = QrCodeLineSetting.QR_CODE_LINE_DRAW_COLOR
    canvas.stroke()
  }
}
</script>

<style lang="stylus" scoped>
.login-qr-code
  width 100%
  height 100%
  display flex
  flex-direction column
  justify-content center
  align-items center
  gap 32px
  background var(--background)

  &[data-role='academy']
    --background var(--color-manager-login__background-academy)
  &[data-role='branch']
    --background var(--color-manager-login__background-branch)

  &__title
    display flex
    flex-direction column
    align-items center
    gap 12px
    color var(--color-manager-login__text)

    &__logo
      width 85px

  &__content
    position relative
    width 374px
    display flex
    flex-direction column
    padding 50px 25px 25px 25px

    &__error
      position absolute
      top 0
      left 25px
      width 324px
      height 25px
      overflow-y scroll

      &__message
        color var(--color-manager-login-alert__text)

    &__reader
      &__camera
        display flex
        flex-direction column
        align-items center
        gap 15px

        &__title
          color var(--color-manager-login__text)

        &__monitor
          position relative
          display flex
          align-items center
          justify-content center
          width 100%

          &__loading
            position absolute
            display flex
            align-items center
            justify-content center
            z-index 1
            width 100%
            height 100%
            background-color var(--color-manager-login-qr-code-loading__background)

            &__text
              color var(--color-manager-login-qr-code-loading__text)

          &__canvas
            width 100%

          &__switch
            position absolute
            top 0
            left 0
            z-index 2

      &__start
        &__button
          display flex
          justify-content center
          height var(--input-height-default)
          color var(--color-manager-login-qr-code-button__text)
          background-color var(--color-manager-login-qr-code-button__background)
          padding 10px
          border-radius 20px
          gap 5px

  &__link
    color var(--color-manager-login__text)
</style>
